/**
 * Berechnet gesetzliche Feiertage in Deutschland pro Jahr und Bundesland.
 *
 * Pure Function — keine externe Library, kein State. Bewegliche Feiertage
 * werden ueber den Gauss-Algorithmus aus dem Osterdatum abgeleitet; fixe
 * Feiertage sind hartkodiert; Bundeslaender-Spezifika kommen ueber ein
 * statisches Map.
 *
 * Aufruf: getHolidays(2026, 'NW') → Map<'YYYY-MM-DD', Label>
 */

/** ISO-3166-2-DE Bundesland-Code (z.B. NW = Nordrhein-Westfalen). */
export type RegionCode = 'BW' | 'BY' | 'BE' | 'BB' | 'HB' | 'HH' | 'HE' | 'MV' | 'NI' | 'NW' | 'RP' | 'SL' | 'SN' | 'ST' | 'SH' | 'TH';

export const REGION_LABELS: Record<RegionCode, string> = {
  BW: 'Baden-Württemberg', BY: 'Bayern',         BE: 'Berlin',           BB: 'Brandenburg',
  HB: 'Bremen',            HH: 'Hamburg',         HE: 'Hessen',           MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',     NW: 'Nordrhein-Westfalen', RP: 'Rheinland-Pfalz', SL: 'Saarland',
  SN: 'Sachsen',           ST: 'Sachsen-Anhalt',  SH: 'Schleswig-Holstein', TH: 'Thüringen',
};

/** Gauss-/Butcher-Algorithmus — Ostersonntag fuer ein gegebenes Jahr (julianisch/gregorianisch). */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Mittwoch vor dem 23. November (Buß- und Bettag, nur SN). */
function bussUndBettag(year: number): Date {
  const d = new Date(year, 10, 23); // 23. Nov, monat=10 (0-based)
  // gehe zurueck zum vorherigen Mittwoch
  const dow = d.getDay(); // 0=So, 3=Mi
  const diff = (dow - 3 + 7) % 7 || 7;
  return addDays(d, -diff);
}

interface HolidayDef {
  label: string;
  /** Wenn leer/undefined: bundesweit. Sonst die Liste der Bundeslaender wo der Tag gesetzlich frei ist. */
  regions?: RegionCode[];
}

/**
 * Liefert alle gesetzlichen Feiertage fuer Jahr + Bundesland als Map iso-Datum -> Label.
 */
export function getHolidays(year: number, region: RegionCode): Map<string, string> {
  const easter = easterSunday(year);
  const entries: Array<[Date, HolidayDef]> = [
    [new Date(year, 0, 1), { label: 'Neujahr' }],
    [new Date(year, 0, 6), { label: 'Heilige Drei Könige', regions: ['BW', 'BY', 'ST'] }],
    [new Date(year, 2, 8), { label: 'Internationaler Frauentag', regions: ['BE', 'MV'] }],
    [addDays(easter, -2), { label: 'Karfreitag' }],
    [addDays(easter, 1), { label: 'Ostermontag' }],
    [new Date(year, 4, 1), { label: 'Tag der Arbeit' }],
    [addDays(easter, 39), { label: 'Christi Himmelfahrt' }],
    [addDays(easter, 50), { label: 'Pfingstmontag' }],
    [addDays(easter, 60), { label: 'Fronleichnam', regions: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL'] }],
    [new Date(year, 7, 15), { label: 'Mariä Himmelfahrt', regions: ['BY', 'SL'] }],
    [new Date(year, 8, 20), { label: 'Weltkindertag', regions: ['TH'] }],
    [new Date(year, 9, 3), { label: 'Tag der Deutschen Einheit' }],
    [new Date(year, 9, 31), { label: 'Reformationstag', regions: ['BB', 'HB', 'HH', 'MV', 'NI', 'SN', 'ST', 'SH', 'TH'] }],
    [new Date(year, 10, 1), { label: 'Allerheiligen', regions: ['BW', 'BY', 'NW', 'RP', 'SL'] }],
    [bussUndBettag(year), { label: 'Buß- und Bettag', regions: ['SN'] }],
    [new Date(year, 11, 25), { label: '1. Weihnachtsfeiertag' }],
    [new Date(year, 11, 26), { label: '2. Weihnachtsfeiertag' }],
  ];

  const map = new Map<string, string>();
  for (const [d, def] of entries) {
    if (!def.regions || def.regions.includes(region)) {
      map.set(toIso(d), def.label);
    }
  }
  return map;
}

/** Schnell-Check: ist ein gegebenes Datum (iso) ein Feiertag in einem Bundesland? */
export function isHoliday(iso: string, region: RegionCode): string | null {
  if (!iso || !/^\d{4}-/.test(iso)) return null;
  const year = parseInt(iso.slice(0, 4), 10);
  const holidays = getHolidays(year, region);
  return holidays.get(iso) ?? null;
}
